/* eslint-disable @typescript-eslint/no-explicit-any */
// https://gist.github.com/mariocesar/e96f6cf6cb2db213173a9c08b9a9867a
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosRequestTransformer,
  AxiosResponse,
  AxiosResponseTransformer,
  Cancel,
  InternalAxiosRequestConfig,
  Method
} from "axios";
import axios, { isCancel as axiosIsCancel } from "axios";
import MockAdapter from "axios-mock-adapter";
import { jwtDecode } from "jwt-decode";
import { refresh } from "./components/Login/utils/refresh";
import { PUBLIC_ROUTES } from "./routes";
import { decamelizeWithoutBreakingFile } from "./utils/decamelizeWithoutBreakingFile";
import { camelizeKeys } from "./utils/SnakeCamelConversion";

export type ApiResponse<T = any, D = any> = AxiosResponse<T, D>;
export interface ApiError<T = unknown, D = any> extends AxiosError<T, D> {
  config?: InternalAxiosRequestConfig<D> & { _isRetry?: boolean };
}

interface MockOptions {
  delayResponse?: number;
}

enum CustomHeaders {
  X_Requested_With = "X-Requested-With",
  Authorization = "Authorization"
}

interface AxiosRequestHeadersExtended extends AxiosRequestHeaders {
  [CustomHeaders.X_Requested_With]?: string;
  [CustomHeaders.Authorization]?: string;
}

interface AccessTokenDecoded {
  exp: number;
  iat: number;
  user_id: number;
}

interface AccessToken {
  value: string;
  expiry: number;
  issuedAt: number;
  expiryInClientTime: number;
}

declare const BACKEND_BASE_URL: string | undefined;
declare const BACKEND_NE_WEB_URL: string | undefined;

export const BASE_URL =
  BACKEND_BASE_URL || `${window.location.protocol}//${window.location.host}/`;
const WITH_CREDENTIALS = typeof BACKEND_BASE_URL === "string";
export const MOCK_RESPONSE_DELAY = 200;

let accessToken: AccessToken | undefined;
let pendingRequests = 0;
const mockAdapterRegistry = new WeakMap<AxiosInstance, MockAdapter>();

if (BACKEND_BASE_URL !== undefined && import.meta.env.MODE !== "test") {
  console.info(`backend base URL: ${BACKEND_BASE_URL}`);
}
if (BACKEND_NE_WEB_URL !== undefined && import.meta.env.MODE !== "test") {
  console.info(`backend ne-web URL: ${BACKEND_NE_WEB_URL}`);
}

function onResponseFulfilled<T>(response: T) {
  pendingRequests = Math.max(0, pendingRequests - 1);
  return Promise.resolve(response);
}

function onResponseRejected(error: ApiError) {
  pendingRequests = Math.max(0, pendingRequests - 1);
  return Promise.reject(error);
}

async function refreshOnResponseRejected(error: ApiError) {
  const initialRequest = error.config;

  // if unauthorized - send request to refresh token
  if (
    error.response?.status === 401 &&
    (error as ApiError<{ code: string }>).response?.data?.code ===
      "token_not_valid" &&
    initialRequest &&
    !error.config?._isRetry
  ) {
    try {
      const success = await refresh();

      if (success) {
        initialRequest.headers = {
          ...initialRequest.headers,
          [CustomHeaders.Authorization]: `Bearer ${getAccessTokenValue()}`
        } as AxiosRequestHeadersExtended;
      }

      return axios(initialRequest); // triggering initial request one more time
    } catch (_) {
      setAccessToken("");
    }
  }

  return Promise.reject(error);
}

const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: WITH_CREDENTIALS,
  transformResponse: [
    ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
    (data) => camelizeKeys(data)
  ],
  transformRequest: [
    (data) => decamelizeWithoutBreakingFile(data),
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[])
  ]
});

instance.interceptors.request.use((config) => config); // instance needs at least one request interceptor for axios-mock-adapter to work properly - this is a no-op
instance.interceptors.response.use(onResponseFulfilled, onResponseRejected);
instance.interceptors.response.use(
  (config) => config,
  refreshOnResponseRejected
);

const withoutCamelizationInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: WITH_CREDENTIALS,
  transformRequest: [
    (data) => decamelizeWithoutBreakingFile(data, true),
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[])
  ]
});

withoutCamelizationInstance.interceptors.request.use((config) => config);
withoutCamelizationInstance.interceptors.response.use(
  onResponseFulfilled,
  onResponseRejected
);
withoutCamelizationInstance.interceptors.response.use(
  (config) => config,
  refreshOnResponseRejected
);

const neWebApiInstance = axios.create({
  baseURL: BACKEND_NE_WEB_URL,
  withCredentials: WITH_CREDENTIALS
});

neWebApiInstance.interceptors.request.use((config) => config);
neWebApiInstance.interceptors.response.use(
  onResponseFulfilled,
  onResponseRejected
);
neWebApiInstance.interceptors.response.use(
  (config) => config,
  refreshOnResponseRejected
);

function getAccessTokenValue() {
  if (accessToken) {
    return accessToken.value;
  } else {
    return "";
  }
}

function setAccessToken(token?: string) {
  if (token) {
    const { exp, iat } = jwtDecode<AccessTokenDecoded>(token);
    const now = Math.floor(Date.now() / 1000);
    /*
      The tokens have an expiry datetime set. The client can use this to determine whether a token is still valid or
      not. However, the client's time might not be in sync with the server's time. Therefore, we need to calculate
      the time drift between the client and the server and adjust the expiry time accordingly.

      The token also has the issued datetime set. We can compare this to the clients datetime and compute an offset
      between server and client (neglecting the network latency).

      Positive values between client times (now) and server times (iat) mean that the client is ahead of the server.
      Hence, for the expiry in the client's time, we need to add the time drift to the servers expiry time (exp),
      which is in the server's time.
      */

    const clientServerTimeDrift = now - iat;
    const expiryInClientTime = exp + clientServerTimeDrift;

    accessToken = {
      value: token,
      expiry: exp,
      issuedAt: iat,
      expiryInClientTime: expiryInClientTime
    };
    console.debug(
      "Got new token, time drift between server and client",
      clientServerTimeDrift
    );
  } else {
    accessToken = undefined;
  }
}

function isAccessTokenValid() {
  if (!accessToken) {
    return false;
  }

  const now: number = Math.floor(Date.now() / 1000);
  // assume that the token is already invalid if it expires in less than 10 seconds
  // this is to reduce the likelihood of performing requests with an expired token
  // the actual expiration might be sooner because of the network latency

  return now < accessToken.expiryInClientTime - 10;
}

async function checkAuthMiddleware() {
  if (
    Object.values(PUBLIC_ROUTES).some((route) =>
      new RegExp(route).test(location.pathname)
    )
  ) {
    return false;
  }

  if (isAccessTokenValid()) {
    return true;
  } else {
    return refresh();
  }
}

function getRequestFunction(instance: AxiosInstance) {
  return async function request<T>(
    method: Method,
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    const isAuthenticated =
      mockAdapterRegistry.has(instance) || (await checkAuthMiddleware());

    if (!isAuthenticated) {
      throw new Error("isAuthenticated error");
    }

    return instance.request<T>({
      ...config,
      method,
      url,
      data,
      headers: {
        ...config?.headers,
        [CustomHeaders.X_Requested_With]: "XMLHttpRequest",
        [CustomHeaders.Authorization]: `Bearer ${getAccessTokenValue()}`
      }
    });
  };
}

function getPublicRequestFunction(instance: AxiosInstance) {
  return function publicRequest<T>(
    method: Method,
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    return instance.request<T>({
      ...config,
      method,
      url,
      data,
      headers: {
        [CustomHeaders.X_Requested_With]: "XMLHttpRequest"
      }
    });
  };
}

function getGetFunction(instance: AxiosInstance) {
  return function get<T = any>(url: string, config?: AxiosRequestConfig) {
    return getRequestFunction(instance)<T>("GET", url, undefined, config);
  };
}

function getPostFunction(instance: AxiosInstance) {
  return function post<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    return getRequestFunction(instance)<T>("POST", url, data, config);
  };
}

function getPutFunction(instance: AxiosInstance) {
  return function putRequest<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    return getRequestFunction(instance)<T>("PUT", url, data, config);
  };
}

function getPatchFunction(instance: AxiosInstance) {
  return function patch<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    return getRequestFunction(instance)<T>("PATCH", url, data, config);
  };
}

function getOptionsFunction(instance: AxiosInstance) {
  return function optionsRequest<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ) {
    return getRequestFunction(instance)<T>("OPTIONS", url, undefined, config);
  };
}

function getDeleteFunction(instance: AxiosInstance) {
  return function deleteRequest<T = any>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    return getRequestFunction(instance)<T>("DELETE", url, data, config);
  };
}

function isCancel(value: Cancel) {
  return axiosIsCancel(value);
}

function getMockFunction(instance: AxiosInstance) {
  return function mock(options: MockOptions = {}) {
    const { delayResponse } = options;
    let mockAdapter = mockAdapterRegistry.get(instance);

    if (!mockAdapter) {
      mockAdapter = new MockAdapter(instance, {
        delayResponse: delayResponse ?? MOCK_RESPONSE_DELAY,
        onNoMatch: "throwException"
      });
      mockAdapterRegistry.set(instance, mockAdapter);
    }

    return mockAdapter;
  };
}

/**
 * Get auth headers for use with raw fetch() requests (e.g. SSE streaming).
 * Call checkAuthMiddleware() before this to ensure the token is fresh.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  await checkAuthMiddleware();
  return {
    "X-Requested-With": "XMLHttpRequest",
    Authorization: `Bearer ${getAccessTokenValue()}`
  };
}

const api = {
  delete: getDeleteFunction(instance),
  get: getGetFunction(instance),
  isAccessTokenValid,
  checkAuthMiddleware,
  isCancel,
  mock: getMockFunction(instance),
  options: getOptionsFunction(instance),
  patch: getPatchFunction(instance),
  post: getPostFunction(instance),
  publicRequest: getPublicRequestFunction(instance),
  put: getPutFunction(instance),
  request: getRequestFunction(instance),
  setAccessToken
};

const apiWithoutCamelization = {
  delete: getDeleteFunction(withoutCamelizationInstance),
  get: getGetFunction(withoutCamelizationInstance),
  mock: getMockFunction(withoutCamelizationInstance),
  options: getOptionsFunction(withoutCamelizationInstance),
  patch: getPatchFunction(withoutCamelizationInstance),
  post: getPostFunction(withoutCamelizationInstance),
  publicRequest: getPublicRequestFunction(withoutCamelizationInstance),
  put: getPutFunction(withoutCamelizationInstance),
  request: getRequestFunction(withoutCamelizationInstance)
};

const neWebApi = {
  delete: getDeleteFunction(neWebApiInstance),
  get: getGetFunction(neWebApiInstance),
  mock: getMockFunction(neWebApiInstance),
  options: getOptionsFunction(neWebApiInstance),
  patch: getPatchFunction(neWebApiInstance),
  post: getPostFunction(neWebApiInstance),
  publicRequest: getPublicRequestFunction(neWebApiInstance),
  put: getPutFunction(neWebApiInstance),
  request: getRequestFunction(neWebApiInstance)
};

export default api;
export { apiWithoutCamelization, neWebApi };
